/**
 * Host editor document operations adapter.
 * This is the ONLY place where editor-specific types may be imported.
 *
 * Responsibilities:
 * - Extract selection context from editor
 * - Find text occurrences in document
 * - Detect document changes for annotations
 * - Get editor ID from DOM
 *
 * Architectural Rules:
 * - MAY import editor-specific types (Editor, Node, Range, etc.)
 * - MUST NOT import Velt SDK types
 * - Uses pure utilities from utils/common.ts where possible
 */
import { Editor, Element as SlateElement, Node, Path } from 'slate';
import { ReactEditor as ReactEditorImpl } from 'slate-react';
import { Constants } from '@/constants/common';
import { kmpSearch } from '@/utils/common';
import { Console } from '@/utils/console';
/**
 * Gets editor ID from editor instance by traversing DOM.
 * Returns null if editor ID attribute not found.
 */
export const getEditorId = (editor) => {
    var _a, _b;
    try {
        // Get the DOM node representing the editor
        // This will fail if editor is not mounted yet (expected during initial creation)
        const domNode = ReactEditorImpl.toDOMNode(editor, editor);
        if (domNode) {
            return ((_b = (_a = domNode === null || domNode === void 0 ? void 0 : domNode.closest(`[${Constants.ATTRIBUTES.EDITOR_ID}]`)) === null || _a === void 0 ? void 0 : _a.getAttribute(Constants.ATTRIBUTES.EDITOR_ID)) !== null && _b !== void 0 ? _b : null);
        }
        // No parent with data-editor-id found
        return null;
    }
    catch (error) {
        // Editor not mounted yet - this is expected during initial editor creation
        // Return null silently, the editorId will be resolved when editor is mounted
        // or a default will be used by the extension
        return null;
    }
};
/**
 * Gets current selection context from editor with full details.
 * Returns null if no selection or selection is collapsed.
 */
export const getCurrentSelectionContext = (editor) => {
    var _a;
    try {
        // Ensure there's a selection
        if (!editor.selection) {
            return null;
        }
        // Get selected text
        const text = Editor.string(editor, editor.selection);
        if (!text) {
            return null;
        }
        // Calculate start and end positions
        const from = Editor.start(editor, editor.selection).offset;
        const to = Editor.end(editor, editor.selection).offset;
        // Find parent node with ID for targetTextNodeId
        const targetTextNode = findParentNodeWithId(editor);
        const targetTextNodeId = targetTextNode === null || targetTextNode === void 0 ? void 0 : targetTextNode.id;
        // Find location ID from DOM
        let locationId;
        if (targetTextNode) {
            locationId =
                ((_a = targetTextNode
                    .closest(`[${Constants.ATTRIBUTES.LOCATION_ID}]`)) === null || _a === void 0 ? void 0 : _a.getAttribute(Constants.ATTRIBUTES.LOCATION_ID)) || undefined;
        }
        // Find occurrence index
        const occurrence = findOccurrenceIndex(editor, text, targetTextNodeId);
        return {
            text,
            from,
            to,
            occurrence,
            targetTextNodeId,
            locationId,
        };
    }
    catch (error) {
        Console.catch('[HostAdapter] Error getting selection context:', error);
        return null;
    }
};
/**
 * Finds occurrence index of selected text in the document.
 * Returns 1-based occurrence index, or 0 if not found.
 */
export const findOccurrenceIndex = (editor, text, targetTextNodeId) => {
    try {
        if (!text || !editor.selection) {
            return 0;
        }
        // If targetTextNodeId is provided, find occurrences within that node
        if (targetTextNodeId) {
            const targetElement = document.getElementById(targetTextNodeId);
            if (targetElement) {
                // Use DOM-based occurrence finding for scoped search
                const startOffset = editor.selection.anchor.offset;
                return findOccurrenceInDOM(targetElement, text, startOffset);
            }
        }
        // Find all matches in the document
        const allMatches = findAllMatches(editor, text);
        const selection = editor.selection;
        if (!selection || allMatches.length === 0) {
            return 0;
        }
        // Check if selection is forward or backward
        const isBackward = Path.compare(selection.anchor.path, selection.focus.path) > 0 ||
            (Path.equals(selection.anchor.path, selection.focus.path) &&
                selection.anchor.offset > selection.focus.offset);
        // Find matching occurrence
        const matchIndex = allMatches.findIndex((match) => {
            if (isBackward) {
                return (Path.equals(match.anchor.path, selection.focus.path) &&
                    Path.equals(match.focus.path, selection.anchor.path) &&
                    match.anchor.offset === selection.focus.offset &&
                    match.focus.offset === selection.anchor.offset);
            }
            else {
                return (Path.equals(match.anchor.path, selection.anchor.path) &&
                    Path.equals(match.focus.path, selection.focus.path) &&
                    match.anchor.offset === selection.anchor.offset &&
                    match.focus.offset === selection.focus.offset);
            }
        });
        return matchIndex !== -1 ? matchIndex + 1 : 0;
    }
    catch (error) {
        Console.catch('[HostAdapter] Error finding occurrence index:', error);
        return 0;
    }
};
/**
 * Finds all text occurrences in the document.
 * Returns array of ranges matching the search text.
 */
export const findTextOccurrences = (editor, text, targetTextNodeId, occurrence) => {
    try {
        if (!text) {
            return [];
        }
        // Find all matches using editor traversal
        const ranges = findAllMatches(editor, text);
        // Convert ranges to OccurrenceMatch format
        const matches = ranges.map((range, index) => {
            const start = Editor.start(editor, range).offset;
            const end = Editor.end(editor, range).offset;
            return {
                index: index + 1, // 1-based
                start,
                end,
                text,
            };
        });
        // Filter by occurrence if specified
        if (occurrence !== undefined && occurrence > 0) {
            const match = matches[occurrence - 1];
            return match ? [match] : [];
        }
        return matches;
    }
    catch (error) {
        Console.catch('[HostAdapter] Error finding text occurrences:', error);
        return [];
    }
};
/**
 * Finds parent node with ID attribute for current selection.
 * Returns HTMLElement with ID, or null if not found.
 */
export const findParentNodeWithId = (editor) => {
    try {
        const { selection } = editor;
        if (!selection) {
            return null;
        }
        const { anchor } = selection;
        const node = Node.get(editor, anchor.path);
        return findParentNodeWithIdFromNode(editor, node, anchor.path);
    }
    catch (error) {
        Console.catch('[HostAdapter] Error finding parent node with ID:', error);
        return null;
    }
};
/**
 * Helper: Finds parent node with ID from a given node.
 */
const findParentNodeWithIdFromNode = (editor, node, path, maxIterations = 100) => {
    try {
        let domNode = null;
        let editorDOM = null;
        try {
            // Convert Slate node to DOM node
            domNode = ReactEditorImpl.toDOMNode(editor, node);
        }
        catch (err) {
            return null;
        }
        try {
            // Get the editor's DOM element
            editorDOM = ReactEditorImpl.toDOMNode(editor, editor);
        }
        catch (err) {
            return null;
        }
        let currentNode = domNode;
        let iterationCount = 0;
        while (currentNode && currentNode !== document.body && iterationCount < maxIterations) {
            // Check if we're still within the editor's DOM
            if (!editorDOM.contains(currentNode)) {
                return null;
            }
            // Check if current node has an ID
            if (currentNode.id) {
                return currentNode;
            }
            currentNode = currentNode.parentElement;
            iterationCount++;
        }
        return null;
    }
    catch (error) {
        Console.catch('[HostAdapter] Error finding parent node:', error);
        return null;
    }
};
/**
 * Helper: Finds occurrence index for a given node and text.
 * This matches the legacy findTextOccurrenceOfNode approach exactly:
 * 1. Find all occurrences of the text in the document (or container)
 * 2. Match the node's path against occurrence paths using Path.equals or Path.isAncestor
 * 3. Return the matching occurrence index (1-based)
 *
 * This is simpler and more reliable than range-based comparison.
 */
const findOccurrenceForNode = (editor, text, node, path, targetTextNodeId) => {
    try {
        if (!text) {
            return 0;
        }
        const nodeText = Node.string(node);
        if (!nodeText || !nodeText.trim()) {
            return 0;
        }
        // Find container node if targetTextNodeId is provided (matching legacy logic)
        let containerNode;
        let containerPath;
        if (targetTextNodeId) {
            const targetElement = document.getElementById(targetTextNodeId);
            if (targetElement) {
                try {
                    containerNode = ReactEditorImpl.toSlateNode(editor, targetElement);
                    containerPath = ReactEditorImpl.findPath(editor, containerNode);
                }
                catch (error) {
                    Console.warn('[findOccurrenceForNode] Could not find container node:', error);
                }
            }
        }
        // Find all occurrences of the text in the document
        // Use findAllMatches which returns ranges with paths
        const allMatches = findAllMatches(editor, text);
        if (allMatches.length === 0) {
            return 0;
        }
        // Filter matches by container if provided (matching legacy findAllTextOccurrences logic)
        let matchesToCheck = allMatches;
        if (containerPath) {
            matchesToCheck = allMatches.filter((match) => {
                const matchStart = Editor.start(editor, match);
                // Check if match is within container by checking if container path is ancestor
                return Path.isAncestor(containerPath, matchStart.path) || Path.equals(containerPath, matchStart.path);
            });
        }
        if (matchesToCheck.length === 0) {
            return 0;
        }
        // Find which occurrence matches the node's path
        // Match using Path.equals or Path.isAncestor (matching legacy logic exactly)
        // Legacy code: const isEqual = Path.equals(occurrence.path, nodePath);
        //              const isDescendant = Path.isAncestor(nodePath, occurrence.path);
        for (let i = 0; i < matchesToCheck.length; i++) {
            const match = matchesToCheck[i];
            const matchStart = Editor.start(editor, match);
            const matchPath = matchStart.path;
            // Check path relationships (matching legacy logic)
            const isEqual = Path.equals(matchPath, path);
            const isDescendant = Path.isAncestor(path, matchPath);
            if (isEqual || isDescendant) {
                // Found the matching occurrence, return 1-based index
                // Need to map back to original allMatches index to get correct occurrence number
                const originalIndex = allMatches.indexOf(match);
                return originalIndex !== -1 ? originalIndex + 1 : i + 1;
            }
        }
        // If no match found, return 0 (matching legacy behavior)
        Console.warn('[findOccurrenceForNode] No matching occurrence found for text:', text, 'node path:', path);
        return 0;
    }
    catch (error) {
        Console.catch('[HostAdapter] Error finding occurrence for node:', error);
        return 0;
    }
};
/**
 * Helper: Finds all matches of text in the document using KMP algorithm.
 */
const findAllMatches = (editor, textToSearch, caseSensitive = true) => {
    try {
        const matches = [];
        if (!textToSearch) {
            return matches;
        }
        // Safely get text from a node
        const getText = (node) => {
            return typeof node === 'object' && node !== null && 'text' in node ? node.text : '';
        };
        // Check if a node is a text node
        const isTextNode = (node) => {
            return (typeof node === 'object' &&
                node !== null &&
                'text' in node &&
                typeof node.text === 'string');
        };
        // Iterate through all text nodes in the document
        const searchInNode = (node, path) => {
            // Handle text nodes
            if (isTextNode(node)) {
                const text = getText(node);
                // Apply case sensitivity
                const searchText = caseSensitive ? textToSearch : textToSearch.toLowerCase();
                const nodeText = caseSensitive ? text : text.toLowerCase();
                // Use KMP algorithm to find all matches in the current text node
                const positions = kmpSearch(nodeText, searchText);
                // Create ranges for each match
                for (const index of positions) {
                    const range = {
                        anchor: { path, offset: index },
                        focus: { path, offset: index + textToSearch.length },
                    };
                    matches.push(range);
                }
            }
            // Recursively search child nodes
            if ('children' in node && Array.isArray(node.children)) {
                node.children.forEach((child, index) => {
                    searchInNode(child, [...path, index]);
                });
            }
        };
        // Start the search from the root of the document
        try {
            const [initialNode] = Editor.nodes(editor, {
                at: [],
                mode: 'highest',
            });
            if (initialNode) {
                searchInNode(initialNode[0], initialNode[1]);
            }
        }
        catch (error) {
            Console.catch('[HostAdapter] Error searching in document:', error);
        }
        return matches;
    }
    catch (error) {
        Console.catch('[HostAdapter] Error finding all matches:', error);
        return [];
    }
};
/**
 * Helper: Finds occurrence in DOM element using KMP algorithm (for scoped searches).
 */
const findOccurrenceInDOM = (element, searchText, startOffset, caseSensitive = true) => {
    try {
        if (!searchText) {
            return 0;
        }
        const fullText = element.textContent || '';
        const processedFullText = caseSensitive ? fullText : fullText.toLowerCase();
        const processedSearchText = caseSensitive ? searchText : searchText.toLowerCase();
        // Use KMP algorithm to find all occurrences
        const occurrences = kmpSearch(processedFullText, processedSearchText);
        if (occurrences.length === 0) {
            return 0;
        }
        // Calculate position (1-based)
        if (startOffset !== undefined) {
            const positionBeforeSelected = occurrences.filter((index) => index < startOffset).length + 1;
            return positionBeforeSelected;
        }
        return 1; // Default to first occurrence
    }
    catch (error) {
        Console.catch('[HostAdapter] Error finding occurrence in DOM:', error);
        return 0;
    }
};
/**
 * Detects document changes for annotations.
 * Compares current document state with stored annotation data.
 */
export const detectDocumentChanges = (editor, transaction, markType = Constants.MARK_NAME, getAnnotationData) => {
    const logPrefix = '[detectDocumentChanges]';
    const changes = new Map();
    try {
        // Collect all annotation nodes in the document
        const annotationNodes = Array.from(Editor.nodes(editor, {
            at: [],
            match: (node) => {
                return (SlateElement.isElement(node) &&
                    node.type === Constants.ELEMENT_TYPE.VELT_COMMENT &&
                    'annotationId' in node);
            },
            mode: 'lowest',
        }));
        for (const [node, path] of annotationNodes) {
            const veltNode = node;
            const annotationId = veltNode.annotationId;
            if (!annotationId) {
                Console.warn(`${logPrefix} Node missing annotationId, skipping`);
                continue;
            }
            // Get stored annotation data
            const storedData = getAnnotationData(annotationId);
            if (!storedData) {
                Console.warn(`${logPrefix} No stored data found for annotationId: ${annotationId}, skipping`);
                continue;
            }
            const originalText = storedData.originalText;
            const originalOccurrence = storedData.originalOccurrence;
            const originalTargetTextNodeId = storedData.targetTextNodeId || '';
            // Extract current text from node
            const currentText = Node.string(node);
            if (!currentText) {
                Console.warn(`${logPrefix} Node has no text content, skipping`);
                continue;
            }
            // Find parent node with ID
            const currentTargetNode = findParentNodeWithIdFromNode(editor, node, path);
            const currentTargetNodeId = (currentTargetNode === null || currentTargetNode === void 0 ? void 0 : currentTargetNode.id) || '';
            // Calculate current occurrence
            // We need to find which occurrence this text represents in the document
            // Since we don't have a selection, we'll use a helper function
            const currentOccurrence = findOccurrenceForNode(editor, currentText, node, path, currentTargetNodeId);
            // Detect changes
            const textChanged = currentText !== originalText;
            const occurrenceChanged = currentOccurrence !== originalOccurrence;
            const targetTextNodeIdChanged = originalTargetTextNodeId !== currentTargetNodeId;
            if (textChanged || occurrenceChanged || targetTextNodeIdChanged) {
                const annotation = storedData.annotation;
                changes.set(annotationId, {
                    annotationId,
                    multiThreadAnnotationId: annotation.multiThreadAnnotationId,
                    originalText,
                    currentText,
                    originalOccurrence,
                    currentOccurrence,
                    originalTargetTextNodeId,
                    newTargetTextNodeId: currentTargetNodeId,
                    annotation,
                    contentChanged: textChanged,
                    occurrenceChanged,
                    targetTextNodeIdChanged,
                });
            }
        }
    }
    catch (error) {
        Console.catch('[HostAdapter] Error detecting document changes:', error);
    }
    return changes;
};
/**
 * Ensures editor is set up (registered and document listeners configured).
 * This is a unified adapter function that handles both registration and setup.
 *
 * @param editorId Optional editor ID (will be resolved from editor if not provided)
 * @param editor The editor instance (typed as unknown to avoid importing editor types in feature modules)
 * @returns Object with editorId string
 *
 * @remarks
 * - This function abstracts editor-specific setup logic
 * - For Slate, just resolves editor ID (document listeners are handled by extension)
 * - Fails silently if setup fails
 */
export const ensureEditorSetup = (editorId, editor) => {
    const logPrefix = '[ensureEditorSetup]';
    try {
        // Resolve editor ID if not provided
        let finalEditorId = editorId;
        if (!finalEditorId) {
            finalEditorId = getEditorId(editor) || 'default';
        }
        // Note: For Slate, document change listeners are handled by the extension
        // No additional setup needed here
        return { editorId: finalEditorId };
    }
    catch (error) {
        Console.warn(`${logPrefix} Error setting up editor:`, error);
        return { editorId: editorId || 'default' };
    }
};
/**
 * Resets the editor selection by focusing the editor.
 * Uses Slate's selection API to reset selection/focus after comment operations.
 *
 * @param editor The editor instance (typed as unknown to avoid importing editor types in feature modules)
 * @returns void
 *
 * @remarks
 * - This function abstracts editor-specific selection reset logic
 * - For Slate, selection reset is handled by the editor itself
 * - Fails silently if reset fails
 */
export const resetEditorSelection = (editor) => {
    const logPrefix = '[resetEditorSelection]';
    try {
        // For Slate, selection reset is typically handled by the editor itself
        // after comment creation, so we don't need to do anything here
        // This matches the comment in the original Slate addComment.ts:
        // "Note: Editor selection reset is handled by the editor itself after comment creation"
    }
    catch (error) {
        Console.warn(`${logPrefix} Error resetting selection:`, error);
    }
};
